import { nomeDoSistema } from '@/lib/sistemaDaClinica';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { GRADE_SUGERIDA, chaveDia, consultasSobRecorrencia, dataParaBr, dentroDoFuncionamento, prazoAte, proximasOcorrencias, recorrenciaValida } from '@/agenda/grade';
import { alterarExcecaoDaAgenda, ouvirClinica, salvarGradeDaAgenda } from '@/services/clinicasService';
import { bloqueioDoDiaInteiro, comFaixa, comFeriado, gradeParaSalvar } from '@/agenda/excecoes';
import { ehBloqueioDeFeriado, ehDiaInteiro } from '@/produtividade/feriados';
import { buscarAgendamentos } from '@/services/agendamentosService';
import { tratarErro } from '@/lib/erros';

/**
 * Grade da agenda de avaliação (VF-1) — só o gestor.
 *
 * ★ O CONTRATO DE OPERAÇÃO está escrito na tela, e não é ressalva: é a condição
 * que faz o número ser verdadeiro. Ver `CONTRATO` abaixo.
 *
 * ⚠ NÃO EXISTE CONCEITO DE ALMOÇO NEM DE INTERVALO. A grade lista os horários
 * que existem; 12:00 e 12:30 simplesmente não estão na lista. Menos conceito,
 * menos coisa para dar errado.
 *
 * ★★★ DUAS GRAVAÇÕES, desde 02/11/2026 (feriado sem meta, decisão P1 do dono):
 *   - **bloquear, remover e "marcar como feriado" gravam NA HORA**, só aquela data
 *     (`alterarExcecaoDaAgenda`, numa transação que lê a data no servidor). ⚠ Antes elas mudavam
 *     só a tela, e sair sem "Salvar grade" perdia o bloqueio — foi o que aconteceu com o 02/11;
 *   - **"Salvar grade" grava só os dias da semana e as recorrências** (`salvarGradeDaAgenda`).
 *     ⚠⚠ Nunca as exceções: com a cópia velha da tela, ela apagaria o feriado recém-gravado.
 * As duas escrevem dentro de `config.agendaAvaliacao` sem tocar no resto do `config`.
 *
 * @param aoMudarSujo ★ avisa a página quando há grade/recorrência NÃO salva — para ela
 *   perguntar antes de trocar de aba (a aba desmonta esta seção).
 */

const DIAS = [
  { n: '1', nome: 'Segunda' }, { n: '2', nome: 'Terça' }, { n: '3', nome: 'Quarta' },
  { n: '4', nome: 'Quinta' }, { n: '5', nome: 'Sexta' }, { n: '6', nome: 'Sábado' },
  { n: '0', nome: 'Domingo' },
];

const listaParaTexto = (lista) =>
  (lista ?? []).map((h) => (h.vagas === 1 ? h.hora : `${h.hora}x${h.vagas}`)).join(' ');

/** "08:00 08:30 14:00x2" → [{hora,vagas}]. Formato de UMA linha, editável a olho. */
function textoParaLista(texto) {
  return String(texto ?? '')
    .split(/[\s,]+/)
    .filter(Boolean)
    .map((pedaco) => {
      const [hora, vagas] = pedaco.split('x');
      return { hora: hora.trim(), vagas: Math.max(1, Number(vagas) || 1) };
    })
    .filter((h) => /^\d{2}:\d{2}$/.test(h.hora))
    .sort((a, b) => a.hora.localeCompare(b.hora));
}

/** Os dias da semana como a gravação os quer — a régua única da comparação "sujo". */
const diasNormalizados = (porDia) => Object.fromEntries(DIAS.map((d) => [d.n, textoParaLista(porDia?.[d.n])]));

export default function SecaoAgenda({ aoMudarSujo } = {}) {
  // As seções de Configurações assinam a clínica cada uma; a única prop é o aviso de "sujo".
  const { clinicaId } = useAuth();
  const [clinica, setClinica] = useState(null);
  const [dias, setDias] = useState({});
  const [novaData, setNovaData] = useState(() => chaveDia(new Date()));
  const [novoMotivo, setNovoMotivo] = useState('');
  // ★ SEM caixa "Dia inteiro". Ela existia e ESCONDIA os campos de hora: nascia
  // marcada, e os campos só apareciam ao desmarcá-la. O dono relatou que o
  // bloqueio por faixa "não existia" — existia, a um clique invisível. Opção
  // escondida atrás de um controle marcado por padrão não é opção oferecida.
  //
  // Agora os campos estão SEMPRE visíveis e o VAZIO é que significa o padrão:
  // sem hora = dia inteiro. O mesmo recado, sem esconder nada.
  const [novoDe, setNovoDe] = useState('');
  const [novoAte, setNovoAte] = useState('');
  const [aceitaFecharTudo, setAceitaFecharTudo] = useState(false);
  // ★ 02/11/2026 — a caixinha "Feriado: sem meta para a equipe" (D1: desmarcada por padrão)
  const [novoFeriado, setNovoFeriado] = useState(false);
  // ★ a data que está sendo gravada agora (bloquear / remover / feriado gravam na hora)
  const [gravando, setGravando] = useState(null);
  const [gravado, setGravado] = useState(null);
  // Recorrência (VF-3, correção 3): REGRA, não datas materializadas.
  const [recorrencias, setRecorrencias] = useState([]);
  const [recDia, setRecDia] = useState('1');
  const [recDe, setRecDe] = useState('08:00');
  const [recAte, setRecAte] = useState('09:00');
  const [recMotivo, setRecMotivo] = useState('');
  const [recMeses, setRecMeses] = useState('3');
  const [conflitos, setConflitos] = useState(null);
  const [aceitaConflitos, setAceitaConflitos] = useState(false);
  const [erro, setErro] = useState(null);
  const [salvo, setSalvo] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (!clinicaId) return undefined;
    return ouvirClinica(clinicaId, setClinica, (e) => {
      console.error('[OdontoSDR] Falha ao ouvir a clínica:', e);
      setErro('Não foi possível carregar a configuração da clínica.');
    });
  }, [clinicaId]);

  const config = clinica?.config?.agendaAvaliacao ?? null;
  /**
   * ★★ As exceções que a lista mostra são as do SERVIDOR (o retrato vivo), não uma cópia
   * editável: elas só mudam pelos botões que gravam na hora.
   */
  const excecoes = (config ?? GRADE_SUGERIDA).excecoes ?? {};

  /**
   * ★★ "SUJO" = a grade ou as recorrências da tela diferem das do banco.
   * ⚠ A base fica também em ref, porque o efeito abaixo precisa comparar a tela com o retrato ANTERIOR
   * no momento em que chega um novo — gravar um bloqueio na hora gera um retrato novo, e ele
   * não pode apagar a vaga que o gestor está editando e ainda não salvou.
   */
  const baseRef = useRef(null);
  const [base, setBase] = useState(null);   // ★ o mesmo valor, em estado: é o que redesenha a faixa
  const telaRef = useRef({ dias: {}, recorrencias: [] });
  telaRef.current = { dias, recorrencias };
  const assinatura = (porDia, recs) => JSON.stringify([diasNormalizados(porDia), recs ?? []]);

  useEffect(() => {
    // ★ A CAUSA DO BUG QUE FECHOU A CLÍNICA: aqui era `config ?? { dias: {} }`,
    // enquanto a AgendaPage usava `config ?? GRADE_SUGERIDA`. Duas leituras
    // diferentes de "não há configuração" — a tela funcionava pela grade
    // sugerida, e esta aba mostrava sete campos EM BRANCO. Quem abrisse para
    // bloquear um feriado e salvasse gravava os sete dias como `[]`, que
    // significa FECHADO. A semana inteira fechou assim, em produção.
    //
    // Semear da MESMA fonte do runtime faz abrir-e-salvar sem tocar em nada ser
    // inócuo, que é a única garantia que interessa.
    const fonte = config ?? GRADE_SUGERIDA;
    const diasDaBase = Object.fromEntries(DIAS.map((d) => [d.n, listaParaTexto(fonte.dias?.[d.n])]));
    const recsDaBase = Array.isArray(fonte.recorrencias) ? fonte.recorrencias : [];
    const anterior = baseRef.current;
    const telaIgualAnterior = anterior === null
      || assinatura(telaRef.current.dias, telaRef.current.recorrencias) === anterior;
    baseRef.current = assinatura(diasDaBase, recsDaBase);
    setBase(baseRef.current);
    // ★ Só re-semeia o que o gestor NÃO está editando: tela suja continua como está.
    if (telaIgualAnterior) {
      setDias(diasDaBase);
      setRecorrencias(recsDaBase);
    }
    setSalvo(false);
  }, [config]);

  const sujo = base !== null && assinatura(dias, recorrencias) !== base;

  /**
   * ★★ A2 (02/11/2026) — A CAIXINHA ACOMPANHA A DATA ESCOLHIDA.
   *
   * ⚠ Ela nascia sempre desmarcada, e "Bloquear o dia inteiro" REGRAVA a data: quem voltasse ao
   * 02/11 só para corrigir o motivo tirava o feriado sem perceber. ★ Agora, escolhendo uma data
   * que já é feriado (ou abrindo a tela num dia que é), ela vem marcada; numa data que não é, vem
   * desmarcada. Quem desmarca de propósito continua decidindo.
   * ★ `semHoras` nas dependências: preencher a hora esconde e desmarca a caixinha (D2); apagar as
   * horas a traz de volta no estado da data — senão ela voltaria desmarcada num feriado.
   */
  const dataEhFeriado = ehBloqueioDeFeriado(excecoes[novaData]);
  const semHoras = !novoDe && !novoAte;
  useEffect(() => {
    if (semHoras) setNovoFeriado(dataEhFeriado);
  }, [novaData, dataEhFeriado, semHoras]);

  useEffect(() => {
    aoMudarSujo?.(sujo);
  }, [sujo, aoMudarSujo]);
  useEffect(() => () => aoMudarSujo?.(false), [aoMudarSujo]);

  /** ★ Fechar a aba, recarregar ou digitar outra URL com a grade não salva: o navegador pergunta. */
  useEffect(() => {
    if (!sujo) return undefined;
    const avisar = (ev) => { ev.preventDefault(); ev.returnValue = ''; };
    window.addEventListener('beforeunload', avisar);
    return () => window.removeEventListener('beforeunload', avisar);
  }, [sujo]);

  const horarioAtendimento = clinica?.config?.horarioAtendimento ?? null;

  /**
   * Avisos, não bloqueios: um horário fora do funcionamento pode ser proposital
   * (atendimento excepcional). Travar seria decidir pela clínica.
   */
  const avisos = useMemo(() => {
    const saida = [];
    for (const d of DIAS) {
      for (const h of textoParaLista(dias[d.n])) {
        if (dentroDoFuncionamento(horarioAtendimento, Number(d.n), h.hora) === false) {
          saida.push(`${d.nome} ${h.hora} está fora do horário de funcionamento da clínica.`);
        }
      }
    }
    return saida;
  }, [dias, horarioAtendimento]);

  /** Os sete dias em branco — que gravariam a clínica fechada todos os dias. */
  const fechariaTudo = useMemo(
    () => DIAS.every((d) => textoParaLista(dias[d.n]).length === 0),
    [dias],
  );

  const DIAS_SEMANA = [
    { n: '1', nome: 'segunda' }, { n: '2', nome: 'terça' }, { n: '3', nome: 'quarta' },
    { n: '4', nome: 'quinta' }, { n: '5', nome: 'sexta' }, { n: '6', nome: 'sábado' },
    { n: '0', nome: 'domingo' },
  ];
  const nomeDoDia = (n) => DIAS_SEMANA.find((d) => d.n === String(n))?.nome ?? '?';

  const adicionarRecorrencia = () => {
    const nova = {
      id: `r${Date.now()}`,
      diaSemana: Number(recDia),
      de: recDe,
      ate: recAte,
      motivo: recMotivo.trim(),
      // ★ PRAZO OBRIGATÓRIO. Recorrência sem fim é a que ninguém lembra de
      // desligar: segue bloqueando a agenda muito depois de o motivo deixar de
      // ser verdade.
      validoAte: prazoAte(recMeses),
      excecoes: [],
    };
    if (!recorrenciaValida(nova)) {
      setErro('Confira o dia, as horas (término depois do início) e o prazo da repetição.');
      return;
    }
    setErro(null);
    setRecorrencias((r) => [...r, nova]);
    setRecMotivo('');
    setSalvo(false);
    setConflitos(null);
    setAceitaConflitos(false);
  };

  const removerRecorrencia = (id) => {
    setRecorrencias((r) => r.filter((x) => x.id !== id));
    setSalvo(false);
  };

  /** A exceção da exceção: esta semana não há. Sem isso, tirar UMA ocorrência
   *  exigiria apagar a série inteira — perdendo prazo e histórico. */
  const pularOcorrencia = (id, data) => {
    if (!data) return;
    setRecorrencias((r) =>
      r.map((x) =>
        x.id === id
          ? { ...x, excecoes: [...new Set([...(x.excecoes ?? []), data])].sort() }
          : x,
      ),
    );
    setSalvo(false);
  };

  const desfazerPulo = (id, data) => {
    setRecorrencias((r) =>
      r.map((x) => (x.id === id ? { ...x, excecoes: (x.excecoes ?? []).filter((d) => d !== data) } : x)),
    );
    setSalvo(false);
  };

  async function salvar() {
    // ⚠ Segunda defesa, independente da semente: fechar a semana inteira é quase
    // sempre engano, e o efeito não é visível na hora de salvar — a tela só
    // deixa de oferecer vaga. Exigir um aceite explícito custa um clique a quem
    // realmente quer fechar, e evita repetir o estrago a quem não quer.
    if (fechariaTudo && !aceitaFecharTudo) {
      setErro(
        'Todos os dias estão sem horário — isso fecha a avaliação a semana inteira. ' +
          'Marque o aceite abaixo se for mesmo o que você quer.',
      );
      return;
    }

    /**
     * ★ AVISO DE CONFLITO: quantas consultas JÁ MARCADAS a repetição atinge.
     *
     * Uma leitura pontual do horizonte, no clique de salvar — não é listener e
     * não roda por render. Descobrir o conflito três semanas depois, quando
     * alguém abrir aquela semana, é pior que uma leitura aqui: a essa altura
     * remarcar já custa uma ligação ao paciente.
     *
     * NÃO bloqueia: a clínica pode ter decidido remarcá-las. Mas ninguém
     * descobre por acaso.
     */
    if (recorrencias.length > 0 && !aceitaConflitos) {
      const limite = recorrencias.map((r) => r.validoAte).sort().at(-1);
      try {
        const [a, m, d] = limite.split('-').map(Number);
        const lista = await buscarAgendamentos(clinicaId, {
          de: new Date(),
          ate: new Date(a, m - 1, d, 23, 59, 59),
        });
        const atingidas = consultasSobRecorrencia({ recorrencias }, lista);
        if (atingidas.length > 0) {
          setConflitos(atingidas);
          setErro(null);
          return;
        }
      } catch (e) {
        // Falha ao consultar não impede salvar a configuração: o aviso é ajuda,
        // não guarda. Seguir sem ele é melhor que travar a tela de configuração.
        // ★ WT 1b: a DECISÃO fica; o que entra é o registro — o dono precisa saber que o
        // aviso de conflito parou de funcionar, mesmo que a tela siga.
        tratarErro('o aviso de conflito da agenda', null, 'service')(e);
      }
    }

    setErro(null);
    setOcupado(true);
    try {
      // ⚠⚠ SÓ dias e recorrências — as exceções já foram gravadas na hora, e a cópia da tela
      // pode estar velha (ver o cabeçalho).
      await salvarGradeDaAgenda(clinicaId, gradeParaSalvar({ dias: diasNormalizados(dias), recorrencias }));
      // ★ a tela passa a ser a base: o retrato que volta do servidor é este mesmo
      baseRef.current = assinatura(dias, recorrencias);
      setBase(baseRef.current);
      setSalvo(true);
    } catch (e) {
      setErro(e.message ?? 'Não foi possível salvar a grade.');
    } finally {
      setOcupado(false);
    }
  }

  const usarSugerida = () => {
    setDias(Object.fromEntries(DIAS.map((d) => [d.n, listaParaTexto(GRADE_SUGERIDA.dias[d.n])])));
    setSalvo(false);
  };

  /**
   * Bloqueia o dia inteiro OU uma faixa de horas.
   *
   * ⚠ NO BLOQUEIO POR FAIXA, `horarios` NÃO É GRAVADO — e isso é a regra, não
   * economia. A ausência da chave é o que faz o dia HERDAR a grade da semana e
   * perder só as horas da faixa. Gravar `horarios: []` junto fecharia o dia
   * inteiro: seria repetir, por outro caminho, o bug que fechou a clínica.
   */
  /** ★ Grava UMA data na hora. `transformar` recebe a exceção do SERVIDOR (ver `agenda/excecoes.js`). */
  async function gravarData(data, transformar, aviso) {
    setErro(null);
    setGravado(null);
    setGravando(data);
    try {
      await alterarExcecaoDaAgenda(clinicaId, data, transformar);
      setGravado(aviso);
      return true;
    } catch (e) {
      tratarErro('o bloqueio da agenda', null, 'service')(e);
      setErro(e?.paraTela ?? `Não foi possível gravar o bloqueio de ${dataParaBr(data)}. Nada mudou — tente de novo.`);
      return false;
    } finally {
      setGravando(null);
    }
  }

  const bloquear = async () => {
    if (!novaData) return;
    // Uma hora sozinha é engano, não intenção: "das 14h" sem fim não diz nada.
    if (Boolean(novoDe) !== Boolean(novoAte)) {
      setErro('Preencha as duas horas, ou deixe as duas em branco para o dia inteiro.');
      return;
    }
    if (novoDe && novoAte && !(novoDe < novoAte)) {
      setErro('A hora de término precisa ser depois da de início.');
      return;
    }
    const data = novaData;
    const de = novoDe;
    const ate = novoAte;
    const motivo = novoMotivo.trim();
    // ★ D2: só o dia inteiro pode ser feriado — com horas, a caixinha nem aparece
    const semMeta = !de && novoFeriado;
    // ★★ A2 — trocar um FERIADO por faixa de horas tira o feriado (D2). Antes era calado; agora
    // pergunta, e cancelar não grava nada (os campos ficam como estão).
    if (de && ehBloqueioDeFeriado(excecoes[data])
      && !window.confirm('Este dia é feriado sem meta. Trocar por faixa de horas tira o feriado. Continuar?')) {
      return;
    }
    const ok = await gravarData(
      data,
      // Sem hora = dia inteiro, na forma que já existia. Faixas se acumulam no mesmo dia.
      (atual) => (de ? comFaixa(atual, { de, ate, motivo }) : bloqueioDoDiaInteiro({ motivo, semMeta })),
      `Bloqueio de ${dataParaBr(data)} gravado${semMeta ? ' (feriado, sem meta)' : ''}.`,
    );
    if (!ok) return;
    setNovoMotivo('');
    setNovoDe('');
    setNovoAte('');
    // ⚠ A caixinha NÃO é zerada aqui: quem decide é o efeito acima, pela data — senão, depois de
    // gravar um feriado, ela ficaria desmarcada na mesma data e o próximo clique o tiraria.
  };

  /** Como a exceção se lê na lista, já em português e com a data em pt-BR. */
  const descreverExcecao = (e) => {
    if (Array.isArray(e)) return `${e.length} horários próprios`;
    if (Array.isArray(e?.bloqueios) && e.bloqueios.length > 0 && e.horarios === undefined) {
      return e.bloqueios.map((b) => `${b.de}–${b.ate}`).join(' e ');
    }
    if (ehBloqueioDeFeriado(e)) return 'dia inteiro · feriado, sem meta';
    if ((e?.horarios ?? []).length === 0) return 'dia inteiro';
    return `${e.horarios.length} horários próprios`;
  };

  const removerExcecao = (data) => {
    // ★ grava na hora: sem "desfazer", então pergunta antes
    if (!window.confirm(`Remover o bloqueio de ${dataParaBr(data)}? A agenda volta a oferecer as vagas desse dia.`)) return;
    gravarData(data, () => null, `Bloqueio de ${dataParaBr(data)} removido.`);
  };

  /** ★ "marcar como feriado" / "não é feriado" — troca só o `semMeta` de um dia inteiro. */
  const trocarFeriado = (data, virarFeriado) => {
    gravarData(
      data,
      (atual) => comFeriado(atual, virarFeriado),
      virarFeriado ? `${dataParaBr(data)} marcado como feriado: sem meta para a equipe.` : `${dataParaBr(data)} deixou de ser feriado: a meta do dia volta.`,
    );
  };

  return (
    <section className="secao">
      <h2 className="secao-titulo">Agenda de avaliação</h2>

      {/* ★ O CONTRATO. Não é ressalva: é a condição que faz o número ser verdadeiro. */}
      <div className="aviso">
        <strong>Avaliação de lead se marca aqui.</strong> Esta grade é a agenda de avaliação
        do marketing, e este sistema é a fonte da verdade dela — por isso a tela de
        agendamento pode dizer "livre" sem ressalva. Se alguém marcar uma avaliação
        direto no {nomeDoSistema(clinica?.config)}, o OdontoSDR não fica sabendo e vai <strong>oferecer a vaga
        como livre</strong>. O número só é verdadeiro enquanto essa regra for cumprida.
        <br />
        A agenda de <strong>tratamento</strong> continua no {nomeDoSistema(clinica?.config)}; é ela que a recepção
        confirma.
      </div>

      <p className="secao-sub">
        Escreva os horários de cada dia separados por espaço. Para mais de uma vaga no mesmo
        horário, use <code>14:00x2</code>. <strong>O horário que não estiver na lista não
        existe</strong> — é assim que o almoço funciona: 12:00 e 12:30 simplesmente não
        entram.
      </p>

      {DIAS.map((d) => (
        <div className="campo" key={d.n}>
          <label htmlFor={`ag-${d.n}`}>
            {d.nome}
            {textoParaLista(dias[d.n]).length > 0 && (
              <span className="canal-instancia">{textoParaLista(dias[d.n]).length} vagas</span>
            )}
          </label>
          <input
            id={`ag-${d.n}`}
            value={dias[d.n] ?? ''}
            placeholder={d.n === '0' ? 'fechado' : '08:00 08:30 09:00 …'}
            disabled={ocupado}
            onChange={(e) => { setDias((x) => ({ ...x, [d.n]: e.target.value })); setSalvo(false); }}
          />
        </div>
      ))}

      {avisos.length > 0 && (
        <p className="campo-ajuda">
          {avisos.map((a) => <span key={a}>{a} </span>)}
          (Aviso, não bloqueio — o horário de funcionamento serve ao atendimento automático
          do WhatsApp, não a esta grade.)
        </p>
      )}

      <button type="button" className="btn-secundario" disabled={ocupado} onClick={usarSugerida}>
        Usar a grade padrão da clínica
      </button>

      <h3 className="secao-titulo">Bloquear um dia ou algumas horas</h3>
      <p className="secao-sub">
        Feriado, reunião, treinamento. O <strong>motivo aparece para quem tentar agendar</strong>,
        para ninguém precisar perguntar por que o dia está fechado.
      </p>

      <div className="linha">
        <div className="campo">
          <label htmlFor="ag-data">Data</label>
          <input id="ag-data" type="date" value={novaData} disabled={ocupado}
            onChange={(e) => setNovaData(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="ag-motivo">Motivo</label>
          <input id="ag-motivo" placeholder="reunião de equipe" value={novoMotivo} disabled={ocupado}
            onChange={(e) => setNovoMotivo(e.target.value)} />
        </div>
        {/* ★ AS HORAS FICAM SEMPRE À VISTA, e em branco significam "dia inteiro".
            Antes elas moravam atrás de uma caixa "Dia inteiro" marcada por
            padrão: o campo existia, ninguém via, e a capacidade foi relatada
            como inexistente. */}
        <div className="campo">
          <label htmlFor="ag-de">Das (opcional)</label>
          <input id="ag-de" type="time" step="1800" value={novoDe} disabled={ocupado}
            onChange={(e) => { setNovoDe(e.target.value); if (e.target.value) setNovoFeriado(false); }} />
        </div>
        <div className="campo">
          <label htmlFor="ag-ate">Até (opcional)</label>
          <input id="ag-ate" type="time" step="1800" value={novoAte} disabled={ocupado}
            onChange={(e) => { setNovoAte(e.target.value); if (e.target.value) setNovoFeriado(false); }} />
        </div>
        <button type="button" className="btn-secundario" disabled={ocupado || gravando !== null || !novaData} onClick={bloquear}>
          {novoDe && novoAte ? `Bloquear das ${novoDe} às ${novoAte}` : 'Bloquear o dia inteiro'}
        </button>
      </div>

      {/* ★★ 02/11/2026 (D1/D2) — só com as horas EM BRANCO: meio dia mantém a meta inteira. */}
      {!novoDe && !novoAte && (
        <label className="campo-inline" htmlFor="ag-feriado">
          <input id="ag-feriado" type="checkbox" checked={novoFeriado} disabled={ocupado}
            onChange={(e) => setNovoFeriado(e.target.checked)} />
          Feriado: sem meta para a equipe
        </label>
      )}

      <p className="campo-ajuda">
        {novoDe && novoAte ? (
          <>
            Só estas horas ficam indisponíveis; o resto do dia continua aberto. A faixa vai de{' '}
            <strong>{novoDe}</strong> até <strong>{novoAte}</strong> <strong>sem incluir</strong>{' '}
            o {novoAte} — às {novoAte} o compromisso acabou e a vaga volta a existir.
          </>
        ) : (
          <>
            <strong>Deixe as horas em branco para fechar o dia inteiro.</strong> Preenchendo as
            duas, só aquela faixa é bloqueada — uma reunião das 14h às 16h não fecha o dia.
            {' '}O bloqueio é <strong>gravado na hora</strong>. Marcando &ldquo;Feriado&rdquo;, a
            equipe fica sem meta nesse dia; o que ela fizer continua contando.
          </>
        )}
      </p>

      <h3 className="secao-titulo">Bloqueio que se repete</h3>
      <p className="secao-sub">
        Uma reunião fixa, por exemplo. Guardamos a <strong>regra</strong> ("toda segunda,
        08:00–09:00, até tal data"), não uma lista de datas — assim ela nunca fica velha nem
        para sozinha no meio do caminho.
      </p>

      <div className="linha">
        <div className="campo">
          <label htmlFor="rec-dia">Toda</label>
          <select id="rec-dia" value={recDia} disabled={ocupado}
            onChange={(e) => setRecDia(e.target.value)}>
            {DIAS_SEMANA.map((d) => (
              <option key={d.n} value={d.n}>{d.nome}</option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="rec-de">Das</label>
          <input id="rec-de" type="time" step="1800" value={recDe} disabled={ocupado}
            onChange={(e) => setRecDe(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="rec-ate">Até</label>
          <input id="rec-ate" type="time" step="1800" value={recAte} disabled={ocupado}
            onChange={(e) => setRecAte(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="rec-prazo">Por quanto tempo</label>
          <select id="rec-prazo" value={recMeses} disabled={ocupado}
            onChange={(e) => setRecMeses(e.target.value)}>
            <option value="3">3 meses</option>
            <option value="6">6 meses</option>
            <option value="12">1 ano</option>
          </select>
        </div>
        <div className="campo">
          <label htmlFor="rec-motivo">Motivo</label>
          <input id="rec-motivo" placeholder="reunião de equipe" value={recMotivo}
            disabled={ocupado} onChange={(e) => setRecMotivo(e.target.value)} />
        </div>
        <button type="button" className="btn-secundario" disabled={ocupado}
          onClick={adicionarRecorrencia}>
          Repetir bloqueio
        </button>
      </div>

      <p className="campo-ajuda">
        Vai valer até <strong>{dataParaBr(prazoAte(recMeses))}</strong>. Depois dessa data a
        repetição para sozinha — o prazo é obrigatório justamente para ninguém esquecer uma
        reunião bloqueando a agenda para sempre.
      </p>

      {recorrencias.length > 0 && (
        <ul className="lista-links">
          {recorrencias.map((r) => (
            <li key={r.id}>
              <strong>toda {nomeDoDia(r.diaSemana)}</strong>
              {` · ${r.de}–${r.ate} · até ${dataParaBr(r.validoAte)}`}
              {r.motivo ? ` · ${r.motivo}` : ''}
              <button type="button" className="btn-texto" disabled={ocupado}
                onClick={() => removerRecorrencia(r.id)}>
                remover série
              </button>

              {/* ★ AS PRÓXIMAS OCORRÊNCIAS, COM AS DATAS REAIS.
                  Antes havia aqui um "não há em {data}" que lia o campo Data do
                  formulário de bloqueio de dia único, no topo da seção: para
                  pular uma semana era preciso digitar a data num campo distante
                  e clicar num botão sem relação visual com ele. A ação tem de
                  carregar o próprio contexto — ou mostrar os alvos concretos
                  sobre os quais age. */}
              <ul className="lista-ocorrencias">
                {proximasOcorrencias(r, 6).map((o) => (
                  <li key={o.data} className={o.pulada ? 'ocorrencia-pulada' : undefined}>
                    {dataParaBr(o.data)}
                    {o.pulada ? (
                      <>
                        {' · sem bloqueio '}
                        <button type="button" className="btn-texto" disabled={ocupado}
                          onClick={() => desfazerPulo(r.id, o.data)}>
                          voltar a bloquear
                        </button>
                      </>
                    ) : (
                      <button type="button" className="btn-texto" disabled={ocupado}
                        onClick={() => pularOcorrencia(r.id, o.data)}>
                        pular esta
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      {conflitos && conflitos.length > 0 && (
        <div className="aviso">
          <strong>Esta repetição atinge {conflitos.length} consulta
          {conflitos.length > 1 ? 's' : ''} já marcada{conflitos.length > 1 ? 's' : ''}.</strong>{' '}
          Elas não somem da agenda — aparecem dentro da célula bloqueada —, mas convém
          remarcá-las.
          <ul className="lista-links">
            {conflitos.slice(0, 8).map((a) => (
              <li key={a.id}>
                {dataParaBr(chaveDia(a.dataHora))} às{' '}
                {String(a.dataHora?.toDate?.()?.getHours() ?? '').padStart(2, '0')}h
              </li>
            ))}
          </ul>
          <label className="campo-inline" htmlFor="rec-aceita">
            <input id="rec-aceita" type="checkbox" checked={aceitaConflitos} disabled={ocupado}
              onChange={(e) => setAceitaConflitos(e.target.checked)} />
            Entendi, salvar mesmo assim.
          </label>
        </div>
      )}

      <h3 className="secao-titulo">Bloqueios marcados</h3>

      {Object.keys(excecoes).length === 0 ? (
        <p className="campo-ajuda">Nenhum dia bloqueado.</p>
      ) : (
        <ul className="lista-links">
          {Object.entries(excecoes).sort().map(([data, e]) => (
            <li key={data}>
              {/* Guardado em ISO (que ORDENA), exibido em pt-BR. A conversão é
                  por string: `new Date('2026-09-10')` é meia-noite UTC e, em
                  UTC-3, mostraria 09/09 — o dia anterior, com cara de certo. */}
              <strong>{dataParaBr(data)}</strong>
              {` · ${descreverExcecao(e)}`}
              {!Array.isArray(e) && e?.motivo ? ` · ${e.motivo}` : ''}
              {ehDiaInteiro(e) && (
                <button type="button" className="btn-texto" disabled={ocupado || gravando !== null}
                  onClick={() => trocarFeriado(data, !ehBloqueioDeFeriado(e))}>
                  {ehBloqueioDeFeriado(e) ? 'não é feriado' : 'marcar como feriado'}
                </button>
              )}
              <button type="button" className="btn-texto" disabled={ocupado || gravando !== null}
                onClick={() => removerExcecao(data)}>
                remover
              </button>
            </li>
          ))}
        </ul>
      )}

      {gravando && <p className="campo-ajuda">Gravando o bloqueio de {dataParaBr(gravando)}…</p>}
      {gravado && !gravando && <p className="campo-ajuda">{gravado}</p>}

      {erro && <p className="erro">{erro}</p>}

      {/* ★★ 02/11/2026 — a grade e as repetições ainda dependem de "Salvar grade". */}
      {sujo && (
        <p className="aviso" role="status">
          <strong>Há alterações não salvas</strong> na grade ou nas repetições. Clique em
          {' '}<strong>Salvar grade</strong>. (Os bloqueios de dia já estão gravados.)
        </p>
      )}

      {fechariaTudo && (
        <label className="campo-inline" htmlFor="ag-aceita-fechar">
          <input id="ag-aceita-fechar" type="checkbox" checked={aceitaFecharTudo} disabled={ocupado}
            onChange={(e) => setAceitaFecharTudo(e.target.checked)} />
          Sim, a clínica não atende avaliação em nenhum dia da semana.
        </label>
      )}

      <footer className="dialogo-acoes">
        {salvo && <span className="campo-ajuda">Salvo.</span>}
        <button type="button" className="btn-primario" disabled={ocupado} onClick={salvar}>
          {ocupado ? 'Salvando…' : 'Salvar grade'}
        </button>
      </footer>
    </section>
  );
}
